import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangePicker3 } from '@components/DateRangePicker/DateRangePicker3';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import {
  useExportCheckinsMutation,
  useExportGlobalCheckinsMutation,
} from '@graphql/generated';
import { downloadBlobFromUrl } from '@utils/downloadBlobFromUrl';

interface ExportActivityTimesheetModalProps {
  visible: boolean;
  onClose: () => void;
  projectId?: string;
}

export const ExportActivityTimesheetModal: React.FC<
  ExportActivityTimesheetModalProps
> = ({ visible, onClose, projectId }) => {
  const { t } = useTranslation('models');

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>();

  const [exportCheckinMutation] = useExportCheckinsMutation();

  const [exportGlobalCheckinsMutation] = useExportGlobalCheckinsMutation();
  const onExport = async (d1: Date | undefined, d2: Date | undefined) => {
    setSelectedDateRange({
      start: d1 ? dayjs(d1) : undefined,
      end: d2 ? dayjs(d2) : undefined,
    });
    setExportError(undefined);
    setIsExporting(true);
    try {
      let url: string | undefined;
      let filename: string | undefined;
      if (projectId) {
        const response = await exportCheckinMutation({
          variables: {
            projectId,
            from: d1,
            to: d2,
          },
        });
        const { exportCheckins: checkins } = response.data ?? {
          exportCheckins: undefined,
        };
        url = checkins?.url;
        filename = checkins?.filename;
      } else {
        const response = await exportGlobalCheckinsMutation({
          variables: {
            from: d1,
            to: d2,
          },
        });

        const { exportGlobalCheckins: checkins } = response.data ?? {
          exportGlobalCheckins: undefined,
        };
        url = checkins?.url;
        filename = checkins?.filename;
      }

      if (url) {
        await downloadBlobFromUrl(url, filename ?? 'filename', {
          notFound: t('files.downloadFileNotFound'),
          networkError: t('files.downloadNetworkError'),
        });
      }
    } catch {
      setExportError(t('activity.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <WebModal
      accessibilityLabel='global-activity-log-export-modal'
      marginTop='m'
      visible={visible}
      width={759}
      height={700}
      title={t('activity.export')}
      titleColor='textPrimary'
      marginHorizontal='m'
      closeDisabled={isExporting}
      onClose={onClose}>
      <Box flex={1} m='m'>
        <Box marginTop='xs'>
          <Text variant='webBodySecondary' color='grey05'>
            {t('activity.exportDescription')}
          </Text>
        </Box>
        {exportError && (
          <Box marginTop='s'>
            <Text variant='error'>{exportError}</Text>
          </Box>
        )}
        <DateRangePicker3
          onSelect={onExport}
          onClose={onClose}
          start={selectedDateRange.start?.toDate()}
          end={selectedDateRange.end?.toDate()}
          isLoading={isExporting}
        />
      </Box>
    </WebModal>
  );
};
